import React, { useEffect, useState } from 'react';
import { Button, Drawer, Icon, Input, Popconfirm, Skeleton, Tag, Upload, message } from 'antd';
import moment from 'moment';
import FileDownload from 'js-file-download';

import { request } from '~/components/Instance';
import { firstError, monthLabel, statusOf } from './helpers';
import {
  DrawerFooter,
  FileRow,
  HistoryItem,
  Missing,
  PdfBadge,
  Section,
  SectionTitle
} from './styles';

// Um mês de um cliente: os PDFs que vieram, o recado e a marcação. Marcar aqui
// grava no registro da tarefa do Calendário, então lá aparece igual.
const MonthDrawer = (props) => {
  const { customerId, competence, onClose, onChanged } = props;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState('');
  const [uploading, setUploading] = useState(false);

  const visible = !!(customerId && competence);

  const apply = (result) => {
    setData(result);
    setNote(result && result.month && result.month.note ? result.month.note : '');
  };

  useEffect(() => {
    if (!visible) {
      setData(null);
      return;
    }
    setLoading(true);
    request('get', 'extrato-bancario/' + customerId + '/' + competence, {}, false)
      .then((response) => apply(response.data.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [customerId, competence, visible]);

  const mark = (status) => {
    if (status === 'nao_entregue' && !note.trim()) {
      message.warning('Escreva o recado: o cliente vê o que falta.');
      return;
    }
    setSaving(true);
    request('put', 'extrato-bancario/' + customerId + '/' + competence, { status: status, note: note.trim() })
      .then((response) => {
        apply(response.data.data);
        if (onChanged) onChanged();
      })
      .catch((error) => message.error(firstError(error, 'Não foi possível salvar.')))
      .finally(() => setSaving(false));
  };

  const download = (file) => {
    request('get', 'extrato-bancario/arquivos/' + file.id, {}, false, { responseType: 'blob' })
      .then((response) => FileDownload(response.data, file.name));
  };

  const remove = (file) => {
    request('delete', 'extrato-bancario/arquivos/' + file.id)
      .then(() => request('get', 'extrato-bancario/' + customerId + '/' + competence, {}, false))
      .then((response) => {
        apply(response.data.data);
        if (onChanged) onChanged();
      });
  };

  // Anexar o que chegou por WhatsApp ou e-mail. Já entra conferido.
  const beforeUpload = (file) => {
    const form = new FormData();
    form.append('files[]', file);
    setUploading(true);
    request('post', 'extrato-bancario/' + customerId + '/' + competence + '/arquivos', form)
      .then((response) => {
        apply(response.data.data);
        if (onChanged) onChanged();
      })
      .catch((error) => message.error(firstError(error, 'Não foi possível anexar.')))
      .finally(() => setUploading(false));
    return false;
  };

  const renderBody = () => {
    if (loading || !data) {
      return <Skeleton active paragraph={{ rows: 8 }} />;
    }

    const look = statusOf(data.month.status);

    return (
      <div style={{ paddingBottom: 80 }}>
        <Section>
          <Tag color={look.color} style={{ fontWeight: 600 }}>{look.label}</Tag>
          {data.month.status === 'nao_entregue' && data.month.note && (
            <div style={{ marginTop: 8, fontSize: 13, color: '#7a4100' }}>
              O cliente vê: “{data.month.note}”
            </div>
          )}
        </Section>

        <Section>
          <SectionTitle>PDFs do mês</SectionTitle>
          {data.files.length === 0 && (
            <div style={{ fontSize: 13, color: '#8c8c8c', marginBottom: 8 }}>Nenhum PDF enviado ainda.</div>
          )}
          {data.files.map((file) => (
            <FileRow key={file.id}>
              <PdfBadge>PDF</PdfBadge>
              <div className="info">
                <span className="name" title={file.name}>{file.name}</span>
                <span className="meta">
                  {file.bank ? file.bank + ' · ' : ''}
                  {moment(file.created_at).format('DD/MM HH:mm')}
                  {file.sent_by ? ' · ' + (file.sent_by_office ? 'equipe: ' : '') + file.sent_by : ''}
                  {file.checked ? '' : ' · falta conferir'}
                </span>
              </div>
              <Button size="small" icon="download" onClick={() => download(file)}>Baixar</Button>
              <Popconfirm title="Apagar este PDF?" okText="Apagar" cancelText="Cancelar" onConfirm={() => remove(file)}>
                <Button size="small" type="danger" ghost icon="delete" aria-label="Apagar PDF" />
              </Popconfirm>
            </FileRow>
          ))}
          {data.missing_banks.length > 0 && (
            <Missing>
              <Icon type="warning" /> Vieram nos últimos meses e não vieram neste: {data.missing_banks.join(', ')}
            </Missing>
          )}
          <Upload accept="application/pdf,.pdf" showUploadList={false} beforeUpload={beforeUpload}>
            <Button icon="paper-clip" loading={uploading}>Anexar PDF recebido por WhatsApp ou e-mail</Button>
          </Upload>
        </Section>

        <Section>
          <SectionTitle>Recado para o cliente</SectionTitle>
          <Input.TextArea
            rows={3}
            maxLength={500}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Ex.: falta o extrato da aplicação do Banco do Brasil"
          />
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>
            Aparece no portal e no e-mail de cobrança quando o mês fica “não entregue”.
          </div>
        </Section>

        {data.history.length > 0 && (
          <Section>
            <SectionTitle>Histórico</SectionTitle>
            {data.history.map((item, index) => (
              <HistoryItem key={index}>
                <span className="when">{item.at ? moment(item.at).format('DD/MM HH:mm') : ''}</span>
                {item.text}
              </HistoryItem>
            ))}
          </Section>
        )}
      </div>
    );
  };

  const status = data && data.month ? data.month.status : null;

  return (
    <Drawer
      visible={visible}
      width={560}
      onClose={onClose}
      destroyOnClose
      title={
        <div>
          <div style={{ fontSize: 12, color: '#8c8c8c' }}>Extrato bancário · {competence ? monthLabel(competence) : ''}</div>
          <div style={{ fontSize: 17 }}>{data ? data.customer.name : ''}</div>
        </div>
      }
    >
      {renderBody()}
      {data && (
        <DrawerFooter>
          {status !== 'pendente' && status !== 'aberto' && status !== 'em_conferencia' && (
            <Button onClick={() => mark('pendente')} disabled={saving}>Desfazer</Button>
          )}
          <Button onClick={() => mark('sem_movimento')} disabled={saving}>Sem movimento</Button>
          <Button type="danger" onClick={() => mark('nao_entregue')} loading={saving}>Não entregue</Button>
          <Button
            type="primary"
            style={{ background: '#1E7B45', borderColor: '#1E7B45' }}
            onClick={() => mark('entregue')}
            loading={saving}
          >
            Entregue
          </Button>
        </DrawerFooter>
      )}
    </Drawer>
  );
};

export default MonthDrawer;
